import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/providers/AuthProvider";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { PageHeading } from "@/components/ui/page-heading";
import { PageContent } from "@/components/ui/page-content";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ErrorDisplay } from "./ui/error";
import { FiMoreVertical, FiPlus } from "react-icons/fi";
import { Assessment, PaginatedResponse } from "@/types";
import {
  getAssessments,
  deleteAssessment,
  cloneAssessment,
} from "@/api/assessments";
import { DataTable, Column } from "@/components/ui/data-table";
import { StatusBadge } from "@/components/ui/status-badge";

export default function Assessments() {
  const navigate = useNavigate();
  const { activeTeam } = useAuth();
  const [assessments, setAssessments] =
    useState<PaginatedResponse<Assessment>>();
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState<string>();

  const fetchAssessments = useCallback(
    async (page: number) => {
      if (!activeTeam) return;

      try {
        const data = await getAssessments(page, 10, activeTeam.id);
        setAssessments(data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsFirstLoad(false);
      }
    },
    [activeTeam]
  );

  useEffect(() => {
    if (activeTeam) {
      fetchAssessments(currentPage);
    }
  }, [currentPage, activeTeam, fetchAssessments]);

  async function handleAction(action: string, assessmentId?: string) {
    if (!assessmentId || !activeTeam) return;

    try {
      switch (action) {
        case "edit":
          navigate(`/assessments/${assessmentId}/edit`);
          break;
        case "clone":
          const clonedAssessment = await cloneAssessment(
            assessmentId,
            activeTeam.id
          );
          navigate(`/assessments/${clonedAssessment.id}`);
          break;
        case "delete":
          setAssessmentToDelete(assessmentId);
          setConfirmDelete(true);
          break;
      }
    } catch (err: any) {
      setError(err.message);
    }
  }

  const columns: Column<Assessment>[] = [
    {
      header: "Title",
      accessorKey: "title",
      cell: (row: Assessment) => row.title,
    },
    {
      header: "Created",
      accessorKey: "created_at",
      cell: (row: Assessment) =>
        new Date(row.created_at || new Date()).toLocaleDateString(),
    },
    {
      header: "Progress",
      accessorKey: "total_candidates",
      cell: (row: Assessment) => (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            {row.total_candidates || 0} candidate
            {row.total_candidates === 1 ? "" : "s"} ·{" "}
            {row.completed_candidates || 0} completed
          </p>
          <Progress
            className="h-2"
            value={
              row.total_candidates
                ? ((row.completed_candidates || 0) / row.total_candidates) * 100
                : 0
            }
          />
        </div>
      ),
    },
    {
      header: "Expiry date",
      accessorKey: "expiry_date",
      cell: (row: Assessment) => new Date().toLocaleDateString(),
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: (row: Assessment) => <StatusBadge status={row.status} />,
    },
    {
      header: "Actions",
      accessorKey: "id",
      cell: (row: Assessment) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div
              className="cursor-pointer hover:opacity-90"
              onClick={(e) => e.stopPropagation()}
            >
              <FiMoreVertical />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleAction("clone", row.id);
              }}
            >
              Clone
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleAction("delete", row.id);
              }}
              className="text-red-600 focus:text-red-600 focus:bg-red-50"
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const handleConfirmDelete = async () => {
    if (assessmentToDelete && activeTeam) {
      try {
        await deleteAssessment(assessmentToDelete, activeTeam.id);
        await fetchAssessments(currentPage);
      } catch (err: any) {
        setError(err.message);
      }
    }
  };

  return (
    <>
      <PageHeading>
        <div className="flex items-center justify-between w-full">
          <div>Assessments</div>
          <Button onClick={() => navigate("/assessments/new")}>
            <FiPlus className="mr-2 h-4 w-4" />
            Create assessment
          </Button>
        </div>
      </PageHeading>
      <PageContent>
        <ErrorDisplay message={error} variant="full" />

        {assessments?.data && assessments.data.length > 0 ? (
          <DataTable
            data={assessments.data}
            columns={columns}
            pagination={assessments?.pagination}
            onPageChange={setCurrentPage}
            onRowClick={(row) => navigate(`/assessments/${row.id}`)}
            isLoading={isFirstLoad}
          />
        ) : (
          !isFirstLoad && (
            <div className="flex flex-col items-center justify-center py-12 px-4 border rounded-lg bg-card">
              <h3 className="text-xl font-semibold mb-2">No assessments yet</h3>
              <p className="text-muted-foreground text-center mb-6">
                Create your first assessment to start evaluating candidates.
              </p>
              <Button onClick={() => navigate("/assessments/new")}>
                <FiPlus className="mr-2 h-4 w-4" />
                Create assessment
              </Button>
            </div>
          )
        )}
      </PageContent>
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete assessment"
        description="Are you sure you want to delete this assessment? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        variant="destructive"
      />
    </>
  );
}
